import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserCog } from "lucide-react";

export default function ProfileEditDialog({ profile, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    name: profile?.name || "",
    phone: profile?.phone || "",
    email: profile?.email || "",
    country_code: profile?.country_code || "AE",
    preferred_currency: profile?.preferred_currency || "AED",
    preferred_channel: profile?.preferred_channel || "WhatsApp",
    marketing_consent: !!profile?.marketing_consent,
  }));
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/customer/me", form);
      toast.success("Profile updated");
      onSaved?.(data);
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="edit-profile-btn"
                  className="rounded-full text-sm h-9 px-4"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border-strong)", color: "var(--text)" }}>
            <UserCog className="h-4 w-4 mr-2" /> Edit profile
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="profile-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit your profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="pe-name">Full name</Label>
              <Input id="pe-name" data-testid="pe-name"
                     value={form.name} onChange={(e) => upd({ name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pe-phone">Mobile</Label>
              <Input id="pe-phone" data-testid="pe-phone"
                     value={form.phone} onChange={(e) => upd({ phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pe-email">Email</Label>
              <Input id="pe-email" data-testid="pe-email" type="email"
                     value={form.email} onChange={(e) => upd({ email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pe-country">Country</Label>
              <Input id="pe-country" data-testid="pe-country" maxLength={3}
                     value={form.country_code}
                     onChange={(e) => upd({ country_code: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pe-ccy">Currency</Label>
              <Input id="pe-ccy" data-testid="pe-currency" maxLength={3}
                     value={form.preferred_currency}
                     onChange={(e) => upd({ preferred_currency: e.target.value.toUpperCase() })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Contact via</Label>
              <Select value={form.preferred_channel}
                      onValueChange={(v) => upd({ preferred_channel: v })}>
                <SelectTrigger data-testid="pe-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}
                    data-testid="pe-save-btn"
                    className="rounded-full text-white"
                    style={{ background: "var(--navy)" }}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
